import { type FieldArrayRenderProps, getIn, setIn } from 'formik';

// Splice-move an item out of `fromIndex` and into `toIndex`, matching Formik's `move` helper.
const moveItem = <T>(array: T[], fromIndex: number, toIndex: number): T[] => {
  const reordered = Array.isArray(array) ? [...array] : [];
  const [moved] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, moved);
  return reordered;
};

/**
 * Reorder a Formik array field WITHOUT triggering `setFieldValue`'s validation.
 *
 * `arrayHelpers.move` remaps `values`, `errors`, and `touched` together, but its `setFieldValue`
 * runs `arrayHelpers.form.validateForm` synchronously on every drop — seconds of lag on this large
 * form. We mirror `move`'s state transition via a single `setFormikState`: reordering can't change
 * any field's validity, so we skip validation but still move the matching `errors`/`touched` entries
 * so validation messages and touched flags follow their row instead of sticking to the old index.
 */
export const reorderArrayField = (arrayHelpers: FieldArrayRenderProps, fromIndex: number, toIndex: number): void => {
  const { name, form } = arrayHelpers;
  // Derive every slice from `prevState` (not a render-closure snapshot), so back-to-back drops
  // before a re-render each build on the previous move instead of the stale initial array.
  form.setFormikState((prevState) => {
    const prevValues = getIn(prevState.values, name);
    const prevErrors = getIn(prevState.errors, name);
    const prevTouched = getIn(prevState.touched, name);
    return {
      ...prevState,
      values: setIn(prevState.values, name, moveItem(prevValues, fromIndex, toIndex)),
      errors: Array.isArray(prevErrors)
        ? setIn(prevState.errors, name, moveItem(prevErrors, fromIndex, toIndex))
        : prevState.errors,
      touched: Array.isArray(prevTouched)
        ? setIn(prevState.touched, name, moveItem(prevTouched, fromIndex, toIndex))
        : prevState.touched,
    };
  });
};
